import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HttpError, type MiceBudgetView, type MiceProjectView } from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { miceRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useOrgContext, useSnackbar } from '@web/ui/core';
import { downloadBlob, fireAndForget } from '@web/ui/core/utils';
import { errorMessage } from './use_mice_orders';

const POLL_MS = 5000;

/** O botão do orçamento na linha de status. */
export interface BudgetLineState {
  /** Já gerado uma vez: o botão vira "Atualizar orçamento". */
  requested: boolean;
  /** Cotando agora (`done < total`); null quando nada está em cotação. */
  quoting: { done: number; total: number } | null;
  generating: boolean;
  onGenerate: () => void;
}

interface UseMiceBudgetOptions {
  /** Lê o orçamento: só com `mice:purchase` e depois de gerado (`budgetRequestedAt`). */
  enabled: boolean;
  /** 409 `premises_missing`: o evento ainda não tem premissas para cotar. */
  onPremisesMissing: () => void;
}

/** Ainda cotando: gerado e com necessidades por cotar. */
const budgetQuoting = (budget: MiceBudgetView | undefined): boolean =>
  budget !== undefined && budget.requestedAt !== null && budget.quoting.done < budget.quoting.total;

const premisesMissing = (err: unknown): boolean =>
  err instanceof HttpError &&
  err.status === 409 &&
  (err.details?.details as { code?: unknown } | undefined)?.code === 'premises_missing';

/** 503 = recomendação desligada no servidor (sem LLM); o texto do servidor nunca vai para a tela. */
const generateErrorMessage = (err: unknown): string =>
  err instanceof HttpError && err.status === 503
    ? t('mice.needs.recommendUnavailable')
    : errorMessage(err);

/** O orçamento do evento: gerar (ou atualizar), acompanhar a cotação e baixar a planilha. */
function useMiceBudget(projectId: string, options: UseMiceBudgetOptions) {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: queryKeys.mice.budget(orgId, projectId),
    queryFn: () => miceRepository.getBudget(orgId, projectId),
    enabled: options.enabled,
    // Relê enquanto o passe do orçamento cota; parado, só quando alguém pede de novo.
    refetchInterval: (q) => (budgetQuoting(q.state.data) ? POLL_MS : false),
  });

  const generate = useMutation({
    mutationFn: () => miceRepository.generateBudget(orgId, projectId),
    // A chave do projeto é prefixo das necessidades, dos viajantes e do orçamento: relê todos.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.mice.project(orgId, projectId) }),
    onError: (err) => {
      if (premisesMissing(err)) {
        options.onPremisesMissing();
        return;
      }
      showSnackbar({ severity: 'error', message: generateErrorMessage(err) });
    },
    meta: { silent: true },
  });

  return {
    budget: options.enabled ? query.data : undefined,
    generate: () => {
      generate.mutate();
    },
    generating: generate.isPending,
    download: () => {
      fireAndForget(
        // O servidor nomeia o arquivo pelo evento (`orcamento-<nome>.xlsx`).
        miceRepository.exportBudget(orgId, projectId).then(({ blob, fileName }) => {
          downloadBlob(blob, fileName ?? 'orcamento.xlsx');
        }),
      );
    },
  };
}

interface BudgetAccess {
  canManage: boolean;
  canPurchase: boolean;
  purchasesOpen: boolean;
}

/**
 * O orçamento na tela do evento. Lê só com `mice:purchase` e depois de gerado; o botão da linha de
 * status é de quem confere premissas e compra (`mice:manage` e `mice:purchase`), com viajantes e
 * as compras abertas (o servidor recusa gerar com as compras encerradas).
 */
export function useEventBudget(
  projectId: string,
  project: MiceProjectView | undefined,
  access: Readonly<BudgetAccess>,
  onPremisesMissing: () => void,
) {
  const requested = Boolean(project?.budgetRequestedAt);
  const budget = useMiceBudget(projectId, {
    enabled: access.canPurchase && requested,
    onPremisesMissing,
  });
  const allowed = access.canManage && access.canPurchase && access.purchasesOpen;
  const line: BudgetLineState | null =
    allowed && project && project.phaseCounts.all > 0
      ? {
          requested,
          quoting: budgetQuoting(budget.budget) ? (budget.budget?.quoting ?? null) : null,
          generating: budget.generating,
          onGenerate: budget.generate,
        }
      : null;
  return { line, budget: budget.budget, download: budget.download };
}
